var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useState } from 'react';
import { createScreenSession, fetchScreenState } from '../api';
const TOKEN_KEY = 'darkside.screenToken';
const POLL_INTERVAL = 1000;
const OFFLINE_AFTER = 10000;
const MAX_RETRY_DELAY = 30000;
// serverTime has a one second resolution, so smaller drifts are just noise.
const OFFSET_TOLERANCE = 2000;
const readToken = () => {
    try {
        return window.localStorage.getItem(TOKEN_KEY);
    }
    catch (_a) {
        return null;
    }
};
const writeToken = (token) => {
    try {
        if (token)
            window.localStorage.setItem(TOKEN_KEY, token);
        else
            window.localStorage.removeItem(TOKEN_KEY);
    }
    catch (_a) {
        // Storage may be unavailable (private mode): the session then lives until reload.
    }
};
export const useScreenSession = () => {
    const [view, setView] = useState({ kind: 'loading' });
    const [offsetMs, setOffsetMs] = useState(0);
    const [offline, setOffline] = useState(false);
    useEffect(() => {
        let cancelled = false;
        let timer = 0;
        let lastOkAt = Date.now();
        let memoryToken = readToken();
        let failedSessions = 0;
        const syncClock = (serverTime) => {
            const next = serverTime * 1000 - Date.now();
            setOffsetMs((prev) => (Math.abs(prev - next) > OFFSET_TOLERANCE ? next : prev));
        };
        const tick = () => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            let delay = POLL_INTERVAL;
            try {
                let token = (_a = readToken()) !== null && _a !== void 0 ? _a : memoryToken;
                if (!token) {
                    const created = yield createScreenSession();
                    if (cancelled)
                        return;
                    token = created.token;
                    memoryToken = token;
                    writeToken(token);
                    setView({ kind: 'pending', code: created.code, expiresAt: created.expiresAt });
                }
                const state = yield fetchScreenState(token);
                if (cancelled)
                    return;
                lastOkAt = Date.now();
                setOffline(false);
                if (state.status === 'pending') {
                    failedSessions = 0;
                    syncClock(state.serverTime);
                    setView({ kind: 'pending', code: state.code, expiresAt: state.expiresAt });
                }
                else if (state.status === 'active') {
                    failedSessions = 0;
                    syncClock(state.serverTime);
                    setView({ kind: 'active', data: state });
                }
                else {
                    // unknown, expired or revoked: forget the token and pair again.
                    memoryToken = null;
                    writeToken(null);
                    failedSessions += 1;
                    delay = failedSessions > 1 ? Math.min(MAX_RETRY_DELAY, POLL_INTERVAL * Math.pow(2, failedSessions)) : 0;
                }
            }
            catch (_b) {
                if (cancelled)
                    return;
                if (Date.now() - lastOkAt > OFFLINE_AFTER)
                    setOffline(true);
            }
            if (!cancelled)
                timer = window.setTimeout(tick, delay);
        });
        tick();
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, []);
    return { view, offsetMs, offline };
};
